// vg-binding: P7.D-44
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/payouts/WithdrawalsHistoryTable.tsx

import { cn } from '@printway/ui';
import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';
import {
  useCancelWithdraw,
  type WalletApiError,
  type WithdrawalRow,
} from '../wallet/api.js';

const STATUS_PILL: Record<string, string> = {
  pending: STATUS_PILL_CLASS.progress,
  approved: STATUS_PILL_CLASS.info,
  rejected: STATUS_PILL_CLASS.danger,
  cancelled: STATUS_PILL_CLASS.done,
  paid_out: STATUS_PILL_CLASS.success,
};

interface Props {
  rows: WithdrawalRow[];
  isLoading?: boolean;
}

function formatTs(ts: number | null): string {
  if (typeof ts !== 'number') return '—';
  return new Date(ts).toLocaleString(undefined);
}

export function WithdrawalsHistoryTable({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('wallet');
  const cancelM = useCancelWithdraw();

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 py-6">
        {Array.from({ length: 5 }).map((_v, i) => (
          <div key={i} className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
        {t('withdrawalsHistory.empty')}
      </p>
    );
  }

  async function handleCancel(id: string): Promise<void> {
    try {
      await cancelM.mutateAsync(id);
    } catch (err) {
      const e = err as WalletApiError;
      alert(e.userMessage ?? e.message);
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-sm">
        <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
          <tr>
            {/* UAT #2189/#2190 — cột Mã rút tiền: deep-link ?focus=<id>
                cần mã hiển thị để tester đối chiếu dòng được highlight. */}
            <th className="px-3 py-2 text-left">{t('withdrawalsHistory.headerCode')}</th>
            <th className="px-3 py-2 text-left">{t('withdrawalsHistory.headerRequestedAt')}</th>
            <th className="px-3 py-2 text-right">{t('withdrawalsHistory.headerAmount')}</th>
            <th className="px-3 py-2 text-left">{t('withdrawalsHistory.headerStatus')}</th>
            <th className="px-3 py-2 text-left">{t('withdrawalsHistory.headerFlag')}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.withdrawal_id} className="border-b border-pw-border" data-testid={`withdrawal-row-${r.withdrawal_id}`}>
              <td className="px-3 py-2 font-mono text-xs text-pw-text">
                {r.withdrawal_id.slice(-8)}
              </td>
              <td className="px-3 py-2 text-xs text-pw-text-muted">{formatTs(r.requested_at)}</td>
              <td className="px-3 py-2 text-right font-medium text-pw-text">{formatCents(r.amount_cents)}</td>
              <td className="px-3 py-2">
                <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold', STATUS_PILL[r.status] ?? '')}>
                  {r.status}
                </span>
              </td>
              <td className="px-3 py-2 text-xs">
                {r.is_flagged_suspicious ? (
                  <span className="text-yellow-700">⚠ {t('withdrawalsHistory.underReview')}</span>
                ) : (
                  <span className="text-pw-text-muted">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {r.status === 'pending' ? (
                  <button
                    type="button"
                    data-testid={`withdrawal-cancel-${r.withdrawal_id}`}
                    onClick={() => void handleCancel(r.withdrawal_id)}
                    disabled={cancelM.isPending}
                    className="rounded-md border border-pw-border bg-pw-bg px-3 py-1 text-xs font-medium text-pw-text-muted hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  >
                    {t('withdrawalsHistory.cancelButton')}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default WithdrawalsHistoryTable;
